import { z } from 'zod';
import { Role, ServerRole, toClientRole } from '../roles/roles.model';

/** Server Care Unit record returned by the API. */
export interface ServerCareUnit {
  readonly id: number;
  name: string;
  priority: number;
  relationshipCount: number;
}

/** Client Care Unit record. */
export interface CareUnit {
  readonly id: number;
  name: string;
  priority: number;
  relationshipCount: number;
}

/** Validates one server Care Unit without coercion and strips unknown properties. */
export const careUnitSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  priority: z.number().int(),
  relationshipCount: z.number().int(),
}) satisfies z.ZodType<ServerCareUnit>;

export type ServerCareUnitCreate = Omit<ServerCareUnit, 'id' | 'relationshipCount' | 'priority'>;
export type CareUnitInput = Omit<CareUnit, 'id' | 'relationshipCount' | 'priority'>;

export const toCreateServerCareUnit = (careUnit: CareUnitInput): ServerCareUnitCreate => ({
  name: careUnit.name,
});

export type RelationshipAssignmentType = 'now' | 'AdminDecides';

export enum RelationshipType {
  CATEGORY = 'Category',
  ONE_TO_ONE = 'One to One',
}

export enum RelationshipSource {
  SSRM_MANAGED = 'SSRM Managed',
  INFO_ONLY = 'Info Only',
}

export interface RelationshipCategory {
  readonly id: number;
  name: string;
}

/** Relationship record returned by the API. */
export interface ServerRelationship {
  readonly id: number;
  externalId: string;
  displayName: string;
  relationshipGroupId: number;
  description: string[];
  externalUsage: {
    displayToStudent: boolean;
    displayToProvider: boolean;
    createElevateRole: boolean;
  };
  relationshipSource: RelationshipSource;
  relationshipType: RelationshipType | null;
  categoryAssignmentType: RelationshipAssignmentType;
  categories: RelationshipCategory[];
  allowMultipleCategories: boolean;
  allowedRoles: (ServerRole & { readonly id: number })[];
  roleAssignmentType: RelationshipAssignmentType;
}

/** Client relationship with externalUsage exposed as fields matching the form. */
export interface Relationship extends Omit<ServerRelationship, 'externalUsage' | 'allowedRoles'> {
  displayToStudents: boolean;
  displayToProvider: boolean;
  createElevateRole: boolean;
  allowedRoles: (Role & { readonly id: number })[];
}

/** Validates API relationships without coercion and strips unknown fields. */
export const serverRelationshipSchema = z.object({
  id: z.number().int(),
  externalId: z.string(),
  displayName: z.string(),
  relationshipGroupId: z.number().int(),
  description: z.array(z.string()),
  externalUsage: z.object({
    displayToStudent: z.boolean(),
    displayToProvider: z.boolean(),
    createElevateRole: z.boolean(),
  }),
  relationshipSource: z.enum(RelationshipSource),
  relationshipType: z.enum(RelationshipType).nullable(),
  categoryAssignmentType: z.enum(['now', 'AdminDecides']),
  categories: z.array(z.object({ id: z.number().int(), name: z.string() })),
  allowMultipleCategories: z.boolean(),
  allowedRoles: z.array(z.object({ id: z.number().int(), externalId: z.string(), name: z.string() })),
  roleAssignmentType: z.enum(['now', 'AdminDecides']),
}) satisfies z.ZodType<ServerRelationship>;

export const toClientRelationship = (server: ServerRelationship): Relationship => ({
  id: server.id,
  externalId: server.externalId,
  displayName: server.displayName,
  relationshipGroupId: server.relationshipGroupId,
  description: [...server.description],
  displayToStudents: server.externalUsage.displayToStudent,
  displayToProvider: server.externalUsage.displayToProvider,
  createElevateRole: server.externalUsage.createElevateRole,
  relationshipSource: server.relationshipSource,
  relationshipType: server.relationshipType,
  categoryAssignmentType: server.categoryAssignmentType,
  categories: server.categories.map((category) => ({ ...category })),
  allowMultipleCategories: server.allowMultipleCategories,
  allowedRoles: server.allowedRoles.map((role) => ({ ...toClientRole(role), id: role.id })),
  roleAssignmentType: server.roleAssignmentType,
});

/** Validates and converts one API relationship to the client model. */
export const relationshipSchema = serverRelationshipSchema.transform(toClientRelationship);

export type RelationshipInput = Omit<Relationship, 'id'>;
export type ServerRelationshipCreate = Omit<ServerRelationship, 'id'>;

export const toCreateServerRelationship = (relationship: RelationshipInput): ServerRelationshipCreate => ({
  externalId: relationship.externalId,
  displayName: relationship.displayName,
  relationshipGroupId: relationship.relationshipGroupId,
  description: [...relationship.description],
  externalUsage: {
    displayToStudent: relationship.displayToStudents,
    displayToProvider: relationship.displayToProvider,
    createElevateRole: relationship.createElevateRole,
  },
  relationshipSource: relationship.relationshipSource,
  relationshipType: relationship.relationshipType,
  categoryAssignmentType: relationship.categoryAssignmentType,
  categories: relationship.categories.map((category) => ({ ...category })),
  allowMultipleCategories: relationship.allowMultipleCategories,
  allowedRoles: relationship.allowedRoles.map((role) => ({
    id: role.id,
    externalId: role.externalId,
    name: role.name,
  })),
  roleAssignmentType: relationship.roleAssignmentType,
});
